import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, DollarSign, FileText, CreditCard, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { translateProvider } from '@/utils/providerTranslations';
import PaymentModal from '@/components/PaymentModal';
import ShovarPreviewModal from '@/components/ShovarPreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryColors = {
  water: { bg: 'bg-gradient-to-br from-cyan-50 to-blue-50', text: 'text-cyan-700', border: 'border-cyan-200/50' },
  electricity: { bg: 'bg-gradient-to-br from-amber-50 to-orange-50', text: 'text-amber-700', border: 'border-amber-200/50' },
  arnona: { bg: 'bg-gradient-to-br from-purple-50 to-violet-50', text: 'text-purple-700', border: 'border-purple-200/50' },
  fines: { bg: 'bg-gradient-to-br from-red-50 to-rose-50', text: 'text-red-700', border: 'border-red-200/50' },
  education: { bg: 'bg-gradient-to-br from-emerald-50 to-green-50', text: 'text-emerald-700', border: 'border-emerald-200/50' },
  health: { bg: 'bg-gradient-to-br from-rose-50 to-pink-50', text: 'text-rose-700', border: 'border-rose-200/50' },
  vaad_bayit: { bg: 'bg-gradient-to-br from-indigo-50 to-blue-50', text: 'text-indigo-700', border: 'border-indigo-200/50' },
  general: { bg: 'bg-gradient-to-br from-slate-50 to-gray-50', text: 'text-slate-700', border: 'border-slate-200/50' }
};

export default function BillDetailPage({ token }) {
  const { t, i18n } = useTranslation();
  const { billId } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [generatingShovar, setGeneratingShovar] = useState(false);
  const [paymentType, setPaymentType] = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showShovarModal, setShowShovarModal] = useState(false);
  const [shovarData, setShovarData] = useState(null);

  useEffect(() => {
    fetchBill();
  }, [billId]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paymentStatus = params.get('payment');

    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', `/bills/${billId}`);
    }
    if (paymentStatus === 'cancelled') {
      toast.error('Payment cancelled');
      window.history.replaceState({}, '', `/bills/${billId}`);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      toast.info('Payment is being processed. Please refresh the page in a moment.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        fetchBill();
        return;
      }

      // Keep polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Status check error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const fetchBill = async () => {
    try {
      const response = await axios.get(`${API}/bills/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBill(response.data);
    } catch (error) {
      toast.error('Failed to load bill details');
      navigate('/bills');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    // Validate partial payment amount if selected
    if (paymentType === 'partial') {
      const amount = parseFloat(customAmount);
      if (!customAmount || isNaN(amount) || amount <= 0 || amount > bill.amount) {
        toast.error('Please enter a valid payment amount');
        return;
      }
    }
    // Open the payment modal
    setShowPaymentModal(true);
  };

  // Get the actual payment amount based on payment type
  const getPaymentAmount = () => {
    if (paymentType === 'partial' && customAmount) {
      return parseFloat(customAmount);
    }
    return bill?.amount || 0;
  };

  const handlePaymentSuccess = async () => {
    // Update the bill status after successful payment
    try {
      const paymentAmount = paymentType === 'full' ? bill.amount : parseFloat(customAmount);
      await axios.post(
        `${API}/bills/${billId}/pay`,
        { amount: paymentAmount, method: 'online' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/');
    } catch (error) {
      // Payment already simulated successfully, just navigate
      navigate('/');
    }
  };

  const handleGenerateShovar = async () => {
    setGeneratingShovar(true);
    try {
      // Download PDF directly from server (most reliable method)
      const response = await axios.get(`${API}/bills/${billId}/shovar/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const shovarNumber = `SHV-${billId.substring(0, 8).toUpperCase()}`;
      const fileName = `SHALEM_Shovar_${shovarNumber}.pdf`;
      
      // Store data for the preview modal
      setShovarData({
        pdfBlob: blob,
        shovarNumber,
        fileName,
        bill: bill
      });
      setShowShovarModal(true);
      
      toast.success(
        <div className="flex flex-col">
          <span className="font-bold">Shovar Generated!</span>
          <span className="text-sm">Number: {shovarNumber}</span>
        </div>
      );
    } catch (error) {
      console.error('Shovar generation error:', error);
      toast.error('Failed to generate Shovar PDF');
    } finally {
      setGeneratingShovar(false);
    }
  };

  const handleCloseShovarModal = () => {
    setShowShovarModal(false);
    // Clean up the blob URL to free memory
    if (shovarData?.blobUrl) {
      URL.revokeObjectURL(shovarData.blobUrl);
    }
    setShovarData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!bill) return null;

  const colors = categoryColors[bill.category] || categoryColors.general;
  const dueDate = new Date(bill.due_date).toLocaleDateString();

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/bills')}
        className="mb-4"
        data-testid="back-to-bills-button"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('bills')}
      </Button>

      {/* Bill Details Card */}
      <Card className={`p-8 rounded-3xl border-2 ${colors.border} ${colors.bg}`} data-testid="bill-detail-card">
        <div className="flex items-start justify-between mb-6">
          <div>
            <Badge className={`${colors.text} mb-3`}>{t(bill.category)}</Badge>
            <h1 className="text-3xl font-extrabold text-slate-900 mb-2" data-testid="bill-provider">
              {translateProvider(bill.provider, i18n.language)}
            </h1>
            <p className="text-slate-600" data-testid="bill-description">{bill.description}</p>
          </div>
          <Badge variant={bill.status === 'paid' ? 'success' : 'destructive'} data-testid="bill-status">
            {t(bill.status)}
          </Badge>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
            <div className="bg-blue-100 p-3 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">{t('amount')}</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="bill-amount">₪{bill.amount.toFixed(2)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
            <div className="bg-amber-100 p-3 rounded-lg">
              <Calendar className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">{t('due_date')}</p>
              <p className="text-xl font-bold text-slate-900" data-testid="bill-due-date">{dueDate}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
            <div className="bg-slate-100 p-3 rounded-lg">
              <FileText className="h-6 w-6 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">{t('category')}</p>
              <p className="text-xl font-bold text-slate-900">{t(bill.category)}</p>
            </div>
          </div>
        </div>

        {bill.status === 'unpaid' && (
          <div className="space-y-4 mt-6">
            {/* Payment Type Selection */}
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <label className="block text-sm font-semibold mb-3">{t('payment_amount')}</label>
              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => setPaymentType('full')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    paymentType === 'full'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid="full-payment-button"
                >
                  {t('full_payment')}
                </button>
                <button
                  onClick={() => setPaymentType('partial')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    paymentType === 'partial'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid="partial-payment-button"
                >
                  {t('partial_payment')}
                </button>
              </div>
              
              {paymentType === 'partial' && (
                <Input
                  type="number"
                  placeholder={`Max: ₪${bill.amount.toFixed(2)}`}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  max={bill.amount}
                  min="1"
                  step="0.01"
                  className="w-full"
                  data-testid="partial-amount-input"
                />
              )}
            </div>

            {/* Action Buttons */}
            <Button
              onClick={handlePayment}
              disabled={paying}
              className="w-full h-14 text-lg font-semibold rounded-full gradient-blue hover:opacity-90 transition-all shadow-lg shadow-blue-600/30"
              data-testid="pay-bill-button"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {paying ? 'Processing...' : `${t('pay_now')} ${paymentType === 'full' ? `₪${bill.amount.toFixed(2)}` : ''}`}
            </Button>

            <Button
              onClick={handleGenerateShovar}
              disabled={generatingShovar}
              variant="outline"
              className="w-full h-12 text-base font-semibold rounded-full border-2 hover:bg-slate-50 mb-20"
              data-testid="generate-shovar-button"
            >
              {generatingShovar ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Download className="h-5 w-5 mr-2" />
              )}
              {generatingShovar ? 'Generating...' : t('generate_shovar')}
            </Button>
          </div>
        )}
      </Card>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        bill={bill}
        paymentAmount={getPaymentAmount()}
        onPaymentSuccess={handlePaymentSuccess}
        token={token}
      />

      {/* Shovar Preview Modal */}
      <ShovarPreviewModal
        isOpen={showShovarModal}
        onClose={handleCloseShovarModal}
        shovarData={shovarData}
      />
    </div>
  );
}